/**
 * @file PageTitle.tsx
 * @module _admin/layout/components/toolbar/page-title/PageTitle
 * @description Renders the PageTitle component that provides route titles and breadcrumb context in the toolbar.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

import clsx from "clsx";
import { Link } from "react-router-dom";
import { useLayout } from "../../../core";
import { usePageData } from "../../../core/PageData";
import { getKey } from "../../../../../Global/history";

const PageTitle = () => {
  const {
    pageTitle,
    pageDescription,
    pageBreadcrumbs,
    previousPageTitle,
    previousPagePath,
  } = usePageData();
  const { config, classes } = useLayout();
  const appPageTitleDirection = config.app?.pageTitle?.direction;

  const breadcrumbs = Array.from(pageBreadcrumbs || []).filter(
    (item) => !item.isSeparator
  );
  const hasBreadcrumbs = breadcrumbs.length > 0;
  const hasPreviousPage = Boolean(previousPageTitle && previousPagePath);

  // Retrieve the navigation state that was saved when navigateTo() last
  // navigated to previousPagePath, so the back link can restore it.
  const previousPageState = (() => {
    if (!previousPagePath) return undefined;
    const raw = getKey('navState__' + previousPagePath);
    if (raw === null || raw === undefined) return undefined;
    if (typeof raw === 'string') {
      try { return JSON.parse(raw); } catch { return undefined; }
    }
    return raw as any;
  })();

  const lastBreadcrumbTitle = hasBreadcrumbs
    ? breadcrumbs[breadcrumbs.length - 1]?.title
    : undefined;
  const shouldShowTitleCrumb =
    !!pageTitle && (!lastBreadcrumbTitle || lastBreadcrumbTitle !== pageTitle);

  return (
    <div
      id="kt_page_title"
      data-kt-swapper="true"
      data-kt-swapper-mode="prepend"
      data-kt-swapper-parent="{default: '#kt_app_content_container', 'lg': '#kt_app_content_container'}"
      className={clsx(
        "page-title d-flex flex-wrap me-3",
        classes.pageTitle.join(" "),
        config.app?.pageTitle?.class,
        {
          "flex-column justify-content-center":
            appPageTitleDirection === "column",
          "align-items-center": appPageTitleDirection !== "column",
        }
      )}
    >
      {config.app?.pageTitle?.breadCrumb && pageTitle && (
        <nav
          className="kt-breadcrumb-nav d-flex px-3 pt-2"
          aria-label="Breadcrumb"
        >
          <ol className="breadcrumb mb-0 kt-breadcrumb align-items-center">
            {/* First item: Home on initial load, then Back to previous page */}
            <li className="breadcrumb-item">
              {hasPreviousPage ? (
                <Link
                  to={previousPagePath as string}
                  state={previousPageState}
                  className="text-gray-500 text-hover-primary d-flex align-items-center"
                >
                  <i
                    className="bi bi-arrow-left-short pe-0 mt-1 fs-26"
                    aria-hidden="true"
                  ></i>
                  <span className="d-none d-sm-inline mt-1 fs-18">
                    {previousPageTitle}
                  </span>
                </Link>
              ) : (
                <Link
                  to="/dashboard"
                  className="text-gray-500 text-hover-primary d-flex align-items-center"
                >
                  <i
                    className="bi bi-house-door-fill me-1 fs-20"
                    aria-hidden="true"
                  ></i>
                  <span className="d-none d-sm-inline mt-1 fs-18">Home</span>
                </Link>
              )}
            </li>

            {/* All breadcrumb items for the current page */}
            {hasBreadcrumbs &&
              breadcrumbs.map((item, index) => (
                <li className="breadcrumb-item" key={`${item.path}${index}`}>
                  <span className="text-gray-700 fw-semibold mt-1 fs-18">
                    {item.title}
                  </span>
                </li>
              ))}

            {/* Current page title as final crumb, unless it duplicates last breadcrumb */}
            {shouldShowTitleCrumb && (
              <li className="breadcrumb-item active" aria-current="page">
                <span className="text-gray-700 fw-semibold mt-1 fs-18">
                  {pageTitle}
                </span>
              </li>
            )}
          </ol>
        </nav>
      )}
    </div>
  );
};

export { PageTitle };
